import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { format } from 'date-fns'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Path } from '../../router/BaseRouter/enums'
import { getInstructorSalaryOverview } from '../../service/instructor-salary.service'
import { InstructorSalaryOverviewItemDto } from '../../service/types'

export const InstructorSalaryOverview: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date())
  const [rows, setRows] = useState<InstructorSalaryOverviewItemDto[]>([])
  const [loading, setLoading] = useState(false)

  const monthString = useMemo(() => format(selectedMonth, 'yyyy-MM'), [selectedMonth])

  useEffect(() => {
    void (async () => {
      setLoading(true)
      try {
        const res = await getInstructorSalaryOverview(monthString)
        setRows(res.data)
      } catch {
        toast.error(t('general.notification.unexpectedError'))
      } finally {
        setLoading(false)
      }
    })()
  }, [monthString, t])

  const handleRowClick = (instructorId: string) => {
    const path = Path.INSTRUCTOR_SALARY_DETAIL.replace(':instructorId', instructorId)
    navigate(`${path}?month=${monthString}`)
  }

  const totalLessonsSalary = rows.reduce((sum, r) => sum + r.totalLessonsSalary, 0)
  const totalSalaryItems = rows.reduce((sum, r) => sum + r.totalSalaryItems, 0)
  const grandTotal = rows.reduce((sum, r) => sum + r.grandTotal, 0)

  return (
    <ScreenWrapper
      title={t('instructorSalaryOverview.title')}
      filter={
        <DatePicker
          views={['year', 'month']}
          value={selectedMonth}
          onChange={(value) => {
            if (value) setSelectedMonth(value)
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
      }
    >
      {loading && <LinearProgress sx={{ mt: 2 }} />}
      <TableContainer component={Paper} sx={{ mt: loading ? 0 : 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('instructorSalaryOverview.table.instructor')}</TableCell>
              <TableCell align="right">{t('instructorSalaryOverview.table.lessonsSalary')}</TableCell>
              <TableCell align="right">{t('instructorSalaryOverview.table.salaryItems')}</TableCell>
              <TableCell align="right">{t('instructorSalaryOverview.table.grandTotal')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('instructorSalaryOverview.noResults')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow
                key={row.instructorId}
                hover
                onClick={() => handleRowClick(row.instructorId)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{row.instructorName}</TableCell>
                <TableCell align="right">{row.totalLessonsSalary.toFixed(2)}</TableCell>
                <TableCell align="right">{row.totalSalaryItems.toFixed(2)}</TableCell>
                <TableCell align="right">{row.grandTotal.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('instructorSalaryOverview.totals')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totalLessonsSalary.toFixed(2)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totalSalaryItems.toFixed(2)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {grandTotal.toFixed(2)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </ScreenWrapper>
  )
}
